import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";

/* ──────────────────────────
   TYPES
────────────────────────── */

export interface ClassifiedProductReview {
  images: boolean;
  title: any;
  guestId: string;
  orderId: string;

  rating: number;
  reviewText: string;

  quality: number;
  deliverySpeed: number;
  accuracy: number;
  communication: number;
  value: number;

  trueToSize?: boolean;
  damaged?: boolean;
  wouldRecommend?: boolean;

  dateCreated: string;
}

export interface ClassifiedSummary {
  avgRating: number;
  quality: number;
  deliverySpeed: number;
  accuracy: number;
  communication: number;
  value: number;

  recommendPercentage: number;
  damageReports: number;

  totalReviews: number;
  lastUpdated: string;
}

interface ClassifiedRatingState {
  reviews: Record<string, ClassifiedProductReview[]>;
  summaries: Record<string, ClassifiedSummary | null>;
  loading: boolean;
  error: string | null;
}

const initialState: ClassifiedRatingState = {
  reviews: {},
  summaries: {},
  loading: false,
  error: null,
};

/* ──────────────────────────
   SUBMIT PRODUCT REVIEW
────────────────────────── */

export const submitClassifiedProductReview = createAsyncThunk(
  "classifiedRatings/submitProductReview",
  async (
    {
      listingId,
      hostId,
      guestId,
      orderId,
      rating,
      reviewText,
      criteria,
      trueToSize,
      damaged,
      wouldRecommend,
    }: {
      listingId: string;
      hostId: string;
      guestId: string;
      orderId: string;
      rating: number;
      reviewText: string;
      criteria: {
        quality: number;
        deliverySpeed: number;
        accuracy: number;
        communication: number;
        value: number;
      };
      trueToSize?: boolean;
      damaged?: boolean;
      wouldRecommend?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const guestRef = doc(
        db,
        "classifiedRatings",
        listingId,
        "reviews",
        guestId
      );

      const snap = await getDoc(guestRef);

      const newReview: ClassifiedProductReview = {
        guestId,
        orderId,
        rating,
        reviewText,
        quality: criteria.quality,
        deliverySpeed: criteria.deliverySpeed,
        accuracy: criteria.accuracy,
        communication: criteria.communication,
        value: criteria.value,
        trueToSize,
        damaged,
        wouldRecommend,
        dateCreated: new Date().toISOString(),
        images: false,
        title: undefined
      };

      // Prevent duplicate per order
      if (snap.exists()) {
        const existing = snap.data();
        const reviews = existing.reviews || [];

        const alreadyReviewed = reviews.some(
          (r: any) => r.orderId === orderId
        );

        if (alreadyReviewed) {
          return { listingId, reviewed: true };
        }

        await updateDoc(guestRef, {
          reviews: [...reviews, newReview],
          lastUpdated: Timestamp.now(),
        });
      } else {
        await setDoc(guestRef, {
          guestId,
          reviews: [newReview],
          createdAt: Timestamp.now(),
        });
      }

      /* ───── UPDATE SUMMARY ───── */

      const listingRef = doc(db, "classifiedRatings", listingId);
      const listingSnap = await getDoc(listingRef);

      const summary: ClassifiedSummary =
        listingSnap.data()?.summary || {
          avgRating: 0,
          quality: 0,
          deliverySpeed: 0,
          accuracy: 0,
          communication: 0,
          value: 0,
          recommendPercentage: 0,
          damageReports: 0,
          totalReviews: 0,
          lastUpdated: new Date().toISOString(),
        };

      const n = summary.totalReviews;
      const newN = n + 1;

      const updatedSummary: ClassifiedSummary = {
        avgRating: (summary.avgRating * n + rating) / newN,
        quality: (summary.quality * n + criteria.quality) / newN,
        deliverySpeed:
          (summary.deliverySpeed * n + criteria.deliverySpeed) / newN,
        accuracy: (summary.accuracy * n + criteria.accuracy) / newN,
        communication:
          (summary.communication * n + criteria.communication) / newN,
        value: (summary.value * n + criteria.value) / newN,

        recommendPercentage:
          (summary.recommendPercentage * n +
            (wouldRecommend ? 100 : 0)) /
          newN,

        damageReports:
          summary.damageReports + (damaged ? 1 : 0),

        totalReviews: newN,
        lastUpdated: new Date().toISOString(),
      };

      if (listingSnap.exists()) {
        await updateDoc(listingRef, {
          summary: updatedSummary,
        });
      } else {
        await setDoc(listingRef, {
          listingId,
          hostId,
          summary: updatedSummary,
          createdAt: Timestamp.now(),
        });
      }

      return {
        listingId,
        review: newReview,
        summary: updatedSummary,
      };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

/* ──────────────────────────
   FETCH REVIEWS BY LISTING
────────────────────────── */

export const fetchClassifiedReviewsByListing = createAsyncThunk(
  "classifiedRatings/fetchByListing",
  async (listingId: string, { rejectWithValue }) => {
    try {
      const colRef = collection(
        db,
        "classifiedRatings",
        listingId,
        "reviews"
      );

      const snap = await getDocs(colRef);

      const allReviews: ClassifiedProductReview[] = [];

      snap.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.reviews) {
          allReviews.push(...data.reviews);
        }
      });

      return { listingId, reviews: allReviews };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchClassifiedReviewByOrder = createAsyncThunk<
  ClassifiedProductReview | null,
  { listingId: string; orderId: string; guestId: string },
  { rejectValue: string }
>(
  "classifiedRatings/fetchByOrder",
  async ({ listingId, orderId, guestId }, { rejectWithValue }) => {
    try {
      const docRef = doc(
        db,
        "classifiedRatings",
        listingId,
        "reviews",
        guestId
      );

      const snap = await getDoc(docRef);

      if (!snap.exists()) return null;

      const data = snap.data();
      const reviews: ClassifiedProductReview[] = data.reviews || [];

      const match = reviews.find(
        (r) => r.orderId === orderId
      );

      return match || null;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

/* ──────────────────────────
   FETCH ALL SUMMARIES
────────────────────────── */

export const fetchAllClassifiedSummaries = createAsyncThunk(
  "classifiedRatings/fetchAllSummaries",
  async (_, { rejectWithValue }) => {
    try {
      const snap = await getDocs(collection(db, "classifiedRatings"));

      const summaries: Record<string, ClassifiedSummary | null> = {};

      snap.forEach((docSnap) => {
        summaries[docSnap.id] =
          docSnap.data().summary || null;
      });

      return summaries;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

/* ──────────────────────────
   SLICE
────────────────────────── */

const classifiedRatingSlice = createSlice({
  name: "classifiedRatings",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      // Submit
      .addCase(submitClassifiedProductReview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        submitClassifiedProductReview.fulfilled,
        (state, action) => {
          if (action.payload.review) {
            const { listingId, review, summary } =
              action.payload;

            if (!state.reviews[listingId]) {
              state.reviews[listingId] = [];
            }

            state.reviews[listingId].push(review);
            state.summaries[listingId] = summary;
          }

          state.loading = false;
        }
      )
      .addCase(
        submitClassifiedProductReview.rejected,
        (state, action) => {
          state.error = action.payload as string;
          state.loading = false;
        }
      )

      // Fetch Reviews
      .addCase(
        fetchClassifiedReviewsByListing.fulfilled,
        (state, action) => {
          state.reviews[action.payload.listingId] =
            action.payload.reviews;
        }
      )

      // Fetch Summaries
      .addCase(
        fetchAllClassifiedSummaries.fulfilled,
        (state, action) => {
          state.summaries = action.payload;
        }
      );
  },
});

export default classifiedRatingSlice.reducer;
